import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ScanSearch,
  LayoutDashboard,
  LogIn,
  LogOut,
  ShieldAlert,
  Menu,
  X,
  UserCircle2,
} from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Navbar() {
  const { pathname, hash } = useLocation();
  const isActive = (p) => pathname === p;
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  // Auto-close the mobile menu whenever the route (or hash) changes
  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  // Lock body scroll while the sheet is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const linkBase = "px-3 py-2 font-medium hover:bg-[#F1F5F9]";
  const desktopLink = (p, label, testId, Icon) => (
    <Link
      to={p}
      data-testid={testId}
      className={`${linkBase} ${
        isActive(p) ? "text-[#0047FF]" : "text-[#0A0A0A]"
      } ${Icon ? "inline-flex items-center gap-1.5" : ""}`}
    >
      {Icon ? <Icon className="w-4 h-4" /> : null}
      {label}
    </Link>
  );

  return (
    <header
      className="sticky top-0 z-40 bg-white border-b border-[#0A0A0A]"
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <span className="w-8 h-8 grid place-items-center bg-[#0047FF] text-white">
            <ScanSearch className="w-5 h-5" strokeWidth={2.4} />
          </span>
          <span className="font-display text-xl tracking-tight">
            Privacy<span className="text-[#0047FF]">Lens</span>
          </span>
          <span className="hidden sm:inline-block ml-1 text-[10px] font-mono uppercase tracking-widest bg-black text-white px-1.5 py-0.5">
            beta
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 text-sm">
          {desktopLink("/recent", "Public Reports", "nav-recent")}
          {desktopLink("/pricing", "Pricing", "nav-pricing")}
          <Link
            to="/#features"
            className={`${linkBase} text-[#0A0A0A]`}
            data-testid="nav-features"
          >
            Features
          </Link>
          {user && desktopLink("/dashboard", "Dashboard", "nav-dashboard", LayoutDashboard)}
          {user?.is_admin && desktopLink("/admin", "Admin", "nav-admin", ShieldAlert)}
        </nav>

        {/* Desktop right-side cluster */}
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <div className="flex items-center gap-2" data-testid="user-menu">
              <Link
                to="/profile"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#0A0A0A] hover:bg-[#F1F5F9]"
                data-testid="user-avatar-link"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || user.email}
                    className="w-6 h-6 border border-[#0A0A0A]"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="w-6 h-6 bg-[#0047FF] text-white grid place-items-center text-[10px] font-mono">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="font-mono text-xs max-w-[160px] truncate">
                  {user.email}
                </span>
              </Link>
              <button
                type="button"
                onClick={logout}
                className="px-2 py-2 text-sm border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                title="Sign out"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-[#0A0A0A] hover:bg-[#F1F5F9]"
                data-testid="nav-signin"
              >
                <LogIn className="w-4 h-4" /> Sign in
              </Link>
              <Link
                to="/"
                className="pl-btn-primary px-4 py-2 text-sm border border-black"
                data-testid="nav-scan-cta"
              >
                Scan a website
              </Link>
            </>
          )}
        </div>

        {/* Mobile right-side: avatar (compact) + hamburger */}
        <div className="md:hidden flex items-center gap-2">
          {user &&
            (user.picture ? (
              <img
                src={user.picture}
                alt={user.name || user.email}
                className="w-7 h-7 border border-[#0A0A0A]"
                referrerPolicy="no-referrer"
                data-testid="mobile-user-avatar"
              />
            ) : (
              <span
                className="w-7 h-7 bg-[#0047FF] text-white grid place-items-center text-xs font-mono"
                data-testid="mobile-user-avatar"
              >
                {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
              </span>
            ))}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
            className="w-10 h-10 grid place-items-center border border-[#0A0A0A] hover:bg-[#F1F5F9]"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="md:hidden fixed inset-0 top-16 bg-black/40 z-30"
            onClick={() => setOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          <nav
            className="md:hidden absolute left-0 right-0 top-16 bg-white border-b border-[#0A0A0A] z-40"
            data-testid="mobile-menu"
          >
            <div className="max-w-[1400px] mx-auto px-4 py-3 flex flex-col">
              {user && (
                <div className="px-3 py-3 border-b border-[#E2E8F0] mb-2 flex items-center gap-3">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name || user.email}
                      className="w-9 h-9 border border-[#0A0A0A]"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="w-9 h-9 bg-[#0047FF] text-white grid place-items-center text-sm font-mono">
                      {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{user.name || user.email}</div>
                    <div className="text-xs text-[#475569] font-mono truncate">{user.email}</div>
                  </div>
                </div>
              )}

              <Link
                to="/recent"
                className={`${linkBase} ${isActive("/recent") ? "text-[#0047FF]" : "text-[#0A0A0A]"}`}
                data-testid="mobile-nav-recent"
              >
                Public Reports
              </Link>
              <Link
                to="/pricing"
                className={`${linkBase} ${isActive("/pricing") ? "text-[#0047FF]" : "text-[#0A0A0A]"}`}
                data-testid="mobile-nav-pricing"
              >
                Pricing
              </Link>
              <Link
                to="/#features"
                className={`${linkBase} text-[#0A0A0A]`}
                data-testid="mobile-nav-features"
              >
                Features
              </Link>

              {user && (
                <Link
                  to="/dashboard"
                  className={`${linkBase} inline-flex items-center gap-2 ${
                    isActive("/dashboard") ? "text-[#0047FF]" : "text-[#0A0A0A]"
                  }`}
                  data-testid="mobile-nav-dashboard"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Link>
              )}
              {user && (
                <Link
                  to="/profile"
                  className={`${linkBase} inline-flex items-center gap-2 ${
                    isActive("/profile") ? "text-[#0047FF]" : "text-[#0A0A0A]"
                  }`}
                  data-testid="mobile-nav-profile"
                >
                  <UserCircle2 className="w-4 h-4" />
                  Profile
                </Link>
              )}
              {user?.is_admin && (
                <Link
                  to="/admin"
                  className={`${linkBase} inline-flex items-center gap-2 ${
                    isActive("/admin") ? "text-[#0047FF]" : "text-[#0A0A0A]"
                  }`}
                  data-testid="mobile-nav-admin"
                >
                  <ShieldAlert className="w-4 h-4" />
                  Admin Console
                </Link>
              )}

              <div className="border-t border-[#E2E8F0] mt-2 pt-3 flex flex-col gap-2">
                {user ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      logout();
                    }}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                    data-testid="mobile-logout-button"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setOpen(false)}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm border border-[#0A0A0A] hover:bg-[#F1F5F9]"
                      data-testid="mobile-nav-signin"
                    >
                      <LogIn className="w-4 h-4" /> Sign in
                    </Link>
                    <Link
                      to="/"
                      className="pl-btn-primary px-4 py-2 text-sm text-center border border-black"
                      data-testid="mobile-nav-scan-cta"
                    >
                      Scan a website
                    </Link>
                  </>
                )}
              </div>
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
